import React, { useState } from 'react';
import { Copy, Eye, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ComponentIdBadge - Compact icon-only component ID display for Super Admins
 * Shows Copy and View icons with hover tooltips
 * 
 * Usage:
 * <ComponentIdBadge 
 *   id="pg-dashboard-main" 
 *   name="Dashboard Main Page"
 *   description="Primary landing page after login"
 * />
 */
export default function ComponentIdBadge({ id, name, description, position = 'top-right', showLabel = false }) {
  const { isSuperAdmin } = useAuth();
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showIdTooltip, setShowIdTooltip] = useState(false);

  // Early return: hide badge for non-super-admins
  if (!isSuperAdmin?.()) {
    return null;
  }

  const handleCopy = async (e) => {
    e?.stopPropagation();
    try {
      await navigator?.clipboard?.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy component ID:', err);
    }
  };

  const handleInfoClick = (e) => {
    e?.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  // Position classes for different placements
  const positionClasses = {
    'top-right': 'absolute top-2 right-2',
    'top-left': 'absolute top-2 left-2',
    'bottom-right': 'absolute bottom-2 right-2',
    'bottom-left': 'absolute bottom-2 left-2',
    'inline': 'inline-flex'
  };

  return (
    <div className={`${positionClasses?.[position]} z-10 flex items-center gap-1`}>
      {/* Compact Icon-Only Badge */}
      <div className="flex items-center gap-0.5 bg-blue-50 border border-blue-200 rounded px-1 py-1 shadow-sm">
        
        {/* Copy Button with Hover Tooltip */}
        <div className="relative group">
          <button
            onClick={handleCopy}
            onMouseEnter={() => setShowIdTooltip(true)}
            onMouseLeave={() => setShowIdTooltip(false)}
            className="p-1 hover:bg-blue-100 rounded transition-colors"
            aria-label="Copy component ID"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-600" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-blue-600" />
            )}
          </button>
          
          {/* Hover Tooltip for Component ID */}
          {showIdTooltip && !copied && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
              <div className="font-medium mb-0.5">Copy Component ID</div>
              <code className="text-xs">{id}</code>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          )}
        </div>

        {/* View Info Button */}
        {(name || description) && (
          <div className="relative">
            <button
              onClick={handleInfoClick}
              className="p-1 hover:bg-blue-100 rounded transition-colors"
              title="View component info"
              aria-label="View component information"
            >
              <Eye className="w-3.5 h-3.5 text-blue-600" />
            </button>

            {/* Info Tooltip */}
            {showTooltip && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                <button
                  onClick={handleInfoClick}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                  aria-label="Close tooltip"
                >
                  <span className="text-lg">&times;</span>
                </button>
                
                <div className="space-y-2">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Component ID</div>
                    <code className="text-xs font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded block">
                      {id}
                    </code>
                  </div>
                  
                  {name && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Component Name</div>
                      <div className="text-xs text-gray-900">{name}</div>
                    </div>
                  )}
                  
                  {description && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Description</div>
                      <div className="text-xs text-gray-700">{description}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Copy Success Feedback */}
      {copied && (
        <span className="text-xs text-green-600 font-medium animate-fade-in">
          ✓
        </span>
      )}
    </div>
  );
}